import { create } from 'zustand';
import { Course, CourseVideo } from '../types';

interface AdminState {
  isAdmin: boolean;
  courses: Course[];
  currentCourse: Course | null;
  videos: CourseVideo[];
  serialNumbers: string[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  checkAdminStatus: () => Promise<void>;
  createCourse: (courseData: Partial<Course>) => Promise<boolean>;
  updateCourse: (courseId: number, courseData: Partial<Course>) => Promise<boolean>;
  deleteCourse: (courseId: number) => Promise<boolean>;
  addVideo: (courseId: number, videoData: Partial<CourseVideo>) => Promise<boolean>;
  updateVideo: (videoId: number, videoData: Partial<CourseVideo>) => Promise<boolean>;
  deleteVideo: (videoId: number) => Promise<boolean>;
  generateSerialNumbers: (courseId: number, count: number) => Promise<boolean>;
  fetchAdminCourses: () => Promise<void>;
  fetchCourseVideos: (courseId: number) => Promise<void>;
  clearError: () => void;
}

const API_BASE = 'https://course-platform-api.fedacreativity.workers.dev';

export const useAdminStore = create<AdminState>((set, get) => ({
  isAdmin: false,
  courses: [],
  currentCourse: null,
  videos: [],
  serialNumbers: [],
  isLoading: false,
  error: null,

  checkAdminStatus: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/status`);
      
      if (response.ok) {
        const data = await response.json();
        set({ isAdmin: data.is_admin || false, isLoading: false });
      } else {
        set({ isAdmin: false, isLoading: false });
      }
    } catch (error) {
      set({ 
        isAdmin: false,
        error: 'فشل في التحقق من صلاحيات الإدارة',
        isLoading: false 
      });
    }
  },

  createCourse: async (courseData: Partial<Course>): Promise<boolean> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(courseData),
      });
      
      if (!response.ok) {
        throw new Error('فشل في إنشاء الكورس');
      }
      
      // إعادة جلب قائمة الكورسات
      await get().fetchAdminCourses();
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
      return false;
    }
  },

  updateCourse: async (courseId: number, courseData: Partial<Course>): Promise<boolean> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/courses/${courseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(courseData),
      });
      
      if (!response.ok) {
        throw new Error('فشل في تحديث الكورس');
      }
      
      await get().fetchAdminCourses();
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
      return false;
    }
  },

  deleteCourse: async (courseId: number): Promise<boolean> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/courses/${courseId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('فشل في حذف الكورس');
      }
      
      await get().fetchAdminCourses();
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
      return false;
    }
  },

  addVideo: async (courseId: number, videoData: Partial<CourseVideo>): Promise<boolean> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/courses/${courseId}/videos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(videoData),
      });
      
      if (!response.ok) {
        throw new Error('فشل في إضافة الفيديو');
      }
      
      await get().fetchCourseVideos(courseId);
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
      return false;
    }
  },

  updateVideo: async (videoId: number, videoData: Partial<CourseVideo>): Promise<boolean> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/videos/${videoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(videoData),
      });
      
      if (!response.ok) {
        throw new Error('فشل في تحديث الفيديو');
      }
      
      set({ isLoading: false });
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
      return false;
    }
  },

  deleteVideo: async (videoId: number): Promise<boolean> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/videos/${videoId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('فشل في حذف الفيديو');
      }
      
      set({ isLoading: false });
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
      return false;
    }
  },

  generateSerialNumbers: async (courseId: number, count: number): Promise<boolean> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/serial-numbers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ course_id: courseId, count }),
      });
      
      if (!response.ok) {
        throw new Error('فشل في إنشاء السيريال نمبر');
      }
      
      const data = await response.json();
      set({ 
        serialNumbers: data.serial_numbers || [],
        isLoading: false 
      });
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
      return false;
    }
  },

  fetchAdminCourses: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/courses`);
      
      if (!response.ok) {
        throw new Error('فشل في جلب قائمة الكورسات');
      }
      
      const data = await response.json();
      set({ 
        courses: data.courses || [],
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
    }
  },

  fetchCourseVideos: async (courseId: number) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/courses/${courseId}/videos`);
      
      if (!response.ok) {
        throw new Error('فشل في جلب مقاطع الفيديو');
      }
      
      const data = await response.json();
      set({ 
        videos: data.videos || [],
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        isLoading: false 
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));